import apiClient from 'ps-components/lib/apiClient';
import { getJobPricingDetails } from '../MarketPricing/JobPricing/JobPricingActions';
import { getParticipationMatches } from '../MarketPricing/ParticipationMatches/ParticipationMatchesActions';
import moment from 'moment';
import fileDownload from 'js-file-download';
import addAlert from 'ps-components/AlertToast/addAlert';

export const START_COMP_SUM_FETCH = 'START_COMP_SUM_FETCH';
export const COMP_SUM_FETCH_SUCCESS = 'COMP_SUM_FETCH_SUCCESS';
export const SAVE_CONFIG = 'SAVE_CONFIG';
export const SET_EMPLOYEE_DATA = 'SET_EMPLOYEE_DATA';
export const SET_MARKET_RANGE_DATA = 'SET_MARKET_RANGE_DATA';
export const SET_PRICING_DATA = 'SET_PRICING_DATA';
export const SET_VIEW_DATA = 'SET_VIEW_DATA';
export const TOGGLE_CONFIG = 'TOGGLE_CONFIG';
export const TOGGLE_REPORT_SECTION = 'TOGGLE_REPORT_SECTION';
export const GET_DISPLAY_PREFERENCES = 'GET_DISPLAY_PREFERENCES';
export const START_DOWNLOADING = 'START_DOWNLOADING';
export const DOWNLOAD_COMPLETE = 'DOWNLOAD_COMPLETE';
export const DOWNLOAD_ERROR = 'DOWNLOAD_ERROR';
export const GET_CONFIG_PREFERENCES = 'GET_CONFIG_PREFERENCES';
export const SET_CSR_SELECTED_BENCHMARK_YEAR = 'SET_CSR_SELECTED_BENCHMARK_YEAR';
export const SET_DISPLAY_IN = 'SET_DISPLAY_IN';
export const SET_LOADING = 'SET_LOADING';
export const RESET_TO_INITIAL_STATE = 'RESET_TO_INITIAL_STATE';
export const UPDATE_PRICING_DATA = 'UPDATE_PRICING_DATA';
export const GET_PRICING_FAILED = 'GET_PRICING_FAILED';
export const GET_PRICING_START = 'GET_PRICING_START';
export const CSR_SET_VIEW_NAME = 'CSR_SET_VIEW_NAME';
export const CSR_SET_SURVEY_VIEW_NAME = 'CSR_SET_SURVEY_VIEW_NAME';
export const PARTICIPATION_MATCHES_GG_RESET = 'PARTICIPATION_MATCHES_GG_RESET';
export const CSR_SET_EMPLOYEE_VIEW_PK = 'CSR_SET_EMPLOYEE_VIEW_PK';
export const CSR_SET_JOB_DETAILS_VIEW_PK = 'CSR_SET_JOB_DETAILS_VIEW_PK';
export const CSR_SET_SHOW_ZERO_WEIGHT_MATCHES = 'CSR_SET_SHOW_ZERO_WEIGHT_MATCHES';

export function downloadToExcel(mpajNewOrOld, requestData) {
  return async dispatch => {
    const { subsite } = window.app;
    dispatch({ type: START_DOWNLOADING });
    try {
      if (mpajNewOrOld === 'OLD') {
        const response = await apiClient.apiPost(`/pricing2/one-page-summary-export`, requestData);
        const data = response.data;

        const filePath = `${window.location.origin}/${subsite}/merit-${subsite}/${data.substring(3)}`;
        window.location.href = filePath;
        dispatch({ type: DOWNLOAD_COMPLETE });
      }
      if (mpajNewOrOld === 'NEW') {
        const response = await apiClient.apiPost('/api/util/report-print/csrToExcel', requestData, {
          responseType: 'arraybuffer'
        });
        fileDownload(response.data, `report-${moment().format('MM-DD-YYYY')}.xlsx`);
        dispatch({ type: DOWNLOAD_COMPLETE });
      }
    } catch (error) {
      console.log('Download to Excel error:', error && error.response);
      addAlert('There was an error generating the excel file', 'danger');
      dispatch({ type: DOWNLOAD_COMPLETE });
    }
  };
}

export function resetToInitialState() {
  return { type: RESET_TO_INITIAL_STATE };
}

export function initCompSum(
  selectedJob,
  replacementTokens,
  selectedBenchmarkYear,
  displayIn,
  search,
  maid,
  currency,
  viewName,
  surveyViewName,
  surveySearch,
  selectedEmployeesViewPk,
  selectedJobDetailsViewPk,
  restrictedUser,
  showZeroWeightMatches
) {
  return async dispatch => {
    dispatch({ type: START_COMP_SUM_FETCH });
    dispatch({ type: PARTICIPATION_MATCHES_GG_RESET });
    dispatch(getConfigSettings());
    if (displayIn) {
      dispatch(setDisplayIn(displayIn));
    }

    try {
      await Promise.all([
        dispatch(getJobPricingDetails(selectedJob, selectedBenchmarkYear, displayIn, search, maid, viewName, showZeroWeightMatches)),
        dispatch(getJobRange(selectedJob, selectedBenchmarkYear, displayIn)),
        dispatch(getJobTopView(selectedJob, replacementTokens, displayIn, selectedJobDetailsViewPk)),
        dispatch(getDisplayPreferences()),
        dispatch(getPMData(currency, selectedJob, maid, selectedBenchmarkYear, displayIn, surveyViewName, surveySearch))
      ]);
      
      if (!restrictedUser) await dispatch(getJobEmployees(selectedJob, displayIn, selectedEmployeesViewPk));
      dispatch({ type: COMP_SUM_FETCH_SUCCESS });
    } catch (error) {
      console.log(error);
    }
  };
}

export function getPMData(currency, selectedJob, maid, selectedBenchmarkYear, displayIn, surveyViewName, surveySearch) {
  return async dispatch => {
    await dispatch(getParticipationMatches(currency, selectedJob, maid)).then(async matches => {
      const options = {
        benchmarkYears: [selectedBenchmarkYear],
        displayIn: displayIn?.toLowerCase(),
        currency: currency,
        surveyViewName,
        data: matches
          ?.filter(j => j.hasData)
          ?.map(j => {
            return {
              cutId: j.cutId
            };
          })
      };

      if (options.data.length > 0) {
        await surveySearch(`/cms/api/accounts/${maid}/survey-jobs`, options, 'post');
      }
    });
  };
}

export function getConfigSettings() {
  return async dispatch => {
    const response = await apiClient.apiGet('/api/home/preferences/getConfigSettings');
    dispatch({ type: GET_CONFIG_PREFERENCES, data: response.data.rows[0] });
  };
}

export function getDisplayPreferences() {
  return async dispatch => {
    const response = await apiClient.apiGet('/api/market-pricing/comp-summary/getDisplayPreferences');
    dispatch({ type: GET_DISPLAY_PREFERENCES, data: response.data });
  };
}

export function getJobEmployees(selectedJob, displayIn, selectedEmployeesViewPk) {
  return async dispatch => {
    const json = {
      params: {
        orgJobCode: selectedJob.code,
        orgJobCodeKey: selectedJob.codeKey,
        viewPk: selectedEmployeesViewPk
      }
    };

    if (displayIn) {
      json.params.displayIn = displayIn;
    }

    const response = await apiClient.apiGet('/api/market-pricing/comp-summary/getJobEmployees', json);
    dispatch({
      type: SET_EMPLOYEE_DATA,
      data: response.data
    });
  };
}

export function getJobPricing(selectedJob, benchmarkYear, displayIn) {
  return async dispatch => {
    dispatch({ type: GET_PRICING_START });
    const json = {
      params: {
        orgJobCode: selectedJob.code || selectedJob.orgJobCode,
        orgJobCodeKey: selectedJob.codeKey || selectedJob.orgJobCodeKey
      }
    };

    if (benchmarkYear) {
      json.params.benchmark_years = [`${benchmarkYear}`];
    }

    if (displayIn) {
      json.params.displayIn = displayIn;
    }

    try {
      const response = await apiClient.apiGet('/api/market-pricing/comp-summary/getJobPricing', json);
      dispatch({ type: SET_PRICING_DATA, data: response.data });
      if (displayIn) {
        dispatch(setDisplayIn(displayIn));
      }
      return response.data;
    } catch (e) {
      dispatch({ type: GET_PRICING_FAILED });
    }
  };
}

export function updatePricingData(data) {
  return { type: UPDATE_PRICING_DATA, data };
}

export function getJobRange(selectedJob, benchmarkYear, displayIn) {
  return async dispatch => {
    const json = {
      params: {
        org_job_id: selectedJob.id,
        orgJobId: selectedJob.id
      }
    };

    if (benchmarkYear) {
      json.params.benchmark_year = benchmarkYear;
    }

    if (displayIn) {
      json.params.displayIn = displayIn;
    }

    const response = await apiClient.apiGet('/api/hris/job-detail-charts/jobRanges', json);
    dispatch({ type: SET_MARKET_RANGE_DATA, data: response.data.chartData });
  };
}

export function getJobTopView(selectedJob, replacementTokens, displayIn, selectedJobDetailsViewPk) {
  return async dispatch => {
    const json = {
      params: {
        orgJobCode: selectedJob.code,
        orgJobCodeKey: selectedJob.codeKey,
        viewPk: selectedJobDetailsViewPk
      }
    };

    if (displayIn) {
      json.params.displayIn = displayIn;
    }

    const response = await apiClient.apiGet('/api/market-pricing/comp-summary/getJobTopView', json);
    dispatch({
      type: SET_VIEW_DATA,
      data: response.data,
      replacementTokens: replacementTokens,
      displayIn: displayIn
    });
  };
}

export function setSelectedBenchmarkYear(benchmarkYear) {
  return async dispatch => {
    dispatch({
      type: SET_CSR_SELECTED_BENCHMARK_YEAR,
      benchmarkYear: benchmarkYear
    });
  };
}

export function setDisplayIn(displayIn) {
  return async dispatch => {
    dispatch({
      type: SET_DISPLAY_IN,
      displayIn
    });
  };
}

export function setViewName(viewName) {
  return async dispatch => {
    dispatch({
      type: CSR_SET_VIEW_NAME,
      viewName
    });
  };
}

export function setSurveyViewName(viewName) {
  return async dispatch => {
    dispatch({
      type: CSR_SET_SURVEY_VIEW_NAME,
      viewName
    });
  };
}

export function setSelectedEmployeesViewPk(selectedEmployeesViewPk) {
  return async dispatch => {
    dispatch({
      type: CSR_SET_EMPLOYEE_VIEW_PK,
      selectedEmployeesViewPk
    });
  };
}

export function setSelectedJobDetailsViewPk(selectedJobDetailsViewPk) {
  return async dispatch => {
    dispatch({
      type: CSR_SET_JOB_DETAILS_VIEW_PK,
      selectedJobDetailsViewPk
    });
  };
}

export function setShowZeroWeightMatches(showZeroWeightMatches) {
  return async dispatch => {
    dispatch({
      type: CSR_SET_SHOW_ZERO_WEIGHT_MATCHES,
      showZeroWeightMatches
    });
  };
}

export function saveConfigSettings(setting, allSettings) {
  return async dispatch => {
    const iframe = document.querySelector('iframe.frame-iframe');
    try {
      dispatch({ type: SAVE_CONFIG, data: setting });
      await apiClient.apiPost('/api/market-pricing/comp-summary/setDisplayPreferences', allSettings);
      if (Object.prototype.hasOwnProperty.call(setting, 'showLastModified') && iframe) {
        const data = {
          showLastModified: setting.showLastModified
        };
        await apiClient.apiPost(`/api/market-pricing/comp-summary/setLastModified`, data);
      }
    } catch (error) {
      console.log(error);
      dispatch({
        type: SAVE_CONFIG,
        data: { [Object.keys(setting)[0]]: !Object.values(setting)[1] }
      });
    }
  };
}

export function toggleConfig() {
  return { type: TOGGLE_CONFIG };
}

export function setLoading() {
  return { type: SET_LOADING };
}

export function toggleReportSection(section, allSettings) {
  return async dispatch => {
    try {
      dispatch({ type: TOGGLE_REPORT_SECTION, data: section });
      await apiClient.apiPost('/api/market-pricing/comp-summary/setDisplayPreferences', allSettings);
    } catch (error) {
      console.log(error);
      dispatch({ type: TOGGLE_REPORT_SECTION, data: section });
    }
  };
}

export function bulkDownload(
  orgJobs,
  logo,
  benchmarkYear,
  reportName,
  customHeader,
  customFooter,
  csrfPair,
  displayIn,
  showCompanyLogo,
  showPayScaleLogo,
  viewName,
  surveyViewName,
  selectedEmployeesViewPk,
  selectedJobDetailsViewPk,
  targetFileType,
  showZeroWeightMatches
) {

  return async dispatch => {
    try {
      dispatch({ type: START_DOWNLOADING });
      const params = {
        orgJobs,
        targetFileType,
        showZeroWeightMatches,
        logo,
        customHeader,
        reportName,
        showCompanyLogo,
        customFooter,
        showPayScaleLogo
      };

      if (benchmarkYear) {
        params.benchmarkYear = benchmarkYear;
      }

      if (displayIn) {
        params.displayIn = displayIn;
      }

      if (viewName) {
        params.viewName = viewName;
      }

      if (surveyViewName) {
        params.surveyViewName = surveyViewName;
      }

      if (selectedEmployeesViewPk) {
        params.selectedEmployeesViewPk = selectedEmployeesViewPk;
      }

      if (selectedJobDetailsViewPk) {
        params.selectedJobDetailsViewPk = selectedJobDetailsViewPk;
      }

      const url = '/api/util/report-print/csrBulkDownload';
      const response = await apiClient.apiPost(url, params);
      if (response.data && response.data.success === true) {
        const fileUrl = response.data.fileUrl;
        const redirectLocation = `${fileUrl}&csrf_token=${csrfPair.csrf_token}&csrf_timestamp=${csrfPair.csrf_timestamp}`;
        window.location = redirectLocation;
        dispatch({ type: DOWNLOAD_COMPLETE });
      } else {
        // How do we want to handle an error case? Getting input from design
        dispatch({ type: DOWNLOAD_COMPLETE });
      }
    } catch (error) {
      // How do we want to handle an error case? Getting input from design
      console.log('downloadPDF error:');
      console.log(error);
      dispatch({ type: DOWNLOAD_COMPLETE });
    }
  };
}

export function downloadPDF(
  orgJobId,
  orgJobCode,
  orgJobCodeKey,
  logo,
  jobTitle,
  benchmarkYear,
  reportName,
  customHeader,
  customFooter,
  csrfPair,
  displayIn,
  showCompanyLogo,
  showPayScaleLogo,
  viewName,
  surveyViewName,
  selectedEmployeesViewPk,
  selectedJobDetailsViewPk,
  showZeroWeightMatches
) {

  return async dispatch => {
    try {
      dispatch({ type: START_DOWNLOADING });

      const url = '/api/util/report-print/csrSingleDownload';
      const params = {
        orgJob: {
          orgJobId,
          orgJobCode,
          orgJobCodeKey
        },
        logo,
        customHeader,
        reportName,
        showCompanyLogo,
        customFooter,
        showPayScaleLogo,
        showZeroWeightMatches
      };

      if (benchmarkYear) {
        params.benchmarkYear = benchmarkYear;
      }

      if (displayIn) {
        params.displayIn = displayIn;
      }

      if (viewName) {
        params.viewName = viewName;
      }

      if (surveyViewName) {
        params.surveyViewName = surveyViewName;
      }

      if (selectedEmployeesViewPk) {
        params.selectedEmployeesViewPk = selectedEmployeesViewPk;
      }

      if (selectedJobDetailsViewPk) {
        params.selectedJobDetailsViewPk = selectedJobDetailsViewPk;
      }

      const response = await apiClient.apiPost(url, params);
      if (response.data && response.data.success === true && response.data.fileUrl) {
        const fileUrl = response.data.fileUrl;
        window.location = `${fileUrl}&csrf_token=${csrfPair.csrf_token}&csrf_timestamp=${csrfPair.csrf_timestamp}`;
        dispatch({ type: DOWNLOAD_COMPLETE });
      } else {
        // How do we want to handle an error case? Getting input from design
        dispatch({ type: DOWNLOAD_COMPLETE });
      }
    } catch (error) {
      // How do we want to handle an error case? Getting input from design
      console.log('downloadPDF error:');
      console.log(error);
      dispatch({ type: DOWNLOAD_COMPLETE });
    }
  };
}

const downloadType = {
  download_type: 'pdf'
};
const downloadUrlKeyWords = ['file_name', 'download_type', 'csrf_token', 'csrf_timestamp'];

export async function redirectToPdf(filename, csrfPair) {
  try {
    // This will be refactored to use the Puppeteer print service
    let downloadLocation = '/api/util/report-print/downloadCSR';
    if (window.location.hostname.indexOf('localhost') === -1) {
      downloadLocation = `/${window.app.subsite}${downloadLocation}`;
    }
    const url = buildUrl(
      downloadLocation,
      { file_name: filename, csrf_token: csrfPair.csrf_token, csrf_timestamp: csrfPair.csrf_timestamp },
      window.app,
      downloadType
    );
    window.location = url;
  } catch (error) {
    // How do we want to handle an error case? Getting input from design
    console.log('redirectToPdf error:');
    console.log(error);
    throw error;
  }
}

// This will be removed in favor of the Puppeteer print service
export function buildUrl(baseUrl, ...dictionaries) {
  if (!dictionaries.length) {
    return baseUrl;
  }

  downloadUrlKeyWords.forEach((key, i) => {
    let value = '';

    dictionaries.forEach(dict => {
      if (!dict) {
        return;
      }

      const foundValue = dict[key];
      if (foundValue) {
        value = foundValue;
        return;
      }
    });

    baseUrl = `${baseUrl}${i ? '&' : '?'}${key}=${value}`;
  });

  return baseUrl;
}
